'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import { UPDATE_LANGUAGE_EVENT } from '@core/constants/EVENTS';
import { globalSharedStyles, tooltipsStyles, typographySharedStyles } from '@core/styles';
import { CUSTOM_FLEX_GROW_ATTRIBUTE } from '@core/constants/ATTRIBUTES';
import { ripple } from '@core/utils/ripple';
import {
    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
    TOOLTIP_POSITION_ATTRIBUTE,
    TOOLTIP_POSITION,
} from '@components/TooltipComponent/CONSTANTS';

import { updateTooltipContent, applyTooltipPositioning } from './utils';
import { PREMIUM_ICON_WEB_COMPONENT } from './CONSTANTS';
import htmlContent from './PremiumIconComponent.html';

/**
 * PremiumIconComponent
 *
 * A web component that provides a premium icon with tooltip functionality.
 * Displays a premium workspace icon with customizable tooltip positioning and content.
 *
 * @class PremiumIconComponent
 * @extends {HTMLElement}
 */
class PremiumIconComponent extends HTMLElement {
    static get observedAttributes() {
        return [
            CUSTOM_FLEX_GROW_ATTRIBUTE.toLowerCase(),
            TOOLTIP_CONTENT_WIDTH_ATTRIBUTE.toLowerCase(),
            TOOLTIP_POSITION_ATTRIBUTE.toLowerCase(),
        ];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            tooltipsStyles,
            typographySharedStyles,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        this._customFlexGrow = 0;
        this._tooltipContentWidth = 0;
        this._tooltipPosition = TOOLTIP_POSITION.TOP_LEFT;

        // Cache DOM references
        this._tooltipDropdownContent = null;
        this._tooltipDropdownContentText = null;
        this._tooltipImgsContentContainer = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
    }

    /**
     * Called when the element is connected to the DOM
     * @returns {void}
     * @override
     */
    connectedCallback() {
        this._cacheDOMElements();
        this._addEventListeners();
        this._addRippleBehavior();
        this.render();
    }

    /**
     * Called when the element is disconnected from the DOM
     * @returns {void}
     * @override
     */
    disconnectedCallback() {
        this._removeEventListeners();
    }

    /**
     * Called when observed attributes change
     * @param {string} name - The attribute name
     * @param {string} oldValue - The old attribute value
     * @param {string} newValue - The new attribute value
     * @returns {void}
     * @override
     */
    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case CUSTOM_FLEX_GROW_ATTRIBUTE.toLowerCase():
                this._customFlexGrow = Number(newValue) || 0;
                break;
            case TOOLTIP_CONTENT_WIDTH_ATTRIBUTE.toLowerCase():
                this._tooltipContentWidth = Number(newValue) || 0;
                break;
            case TOOLTIP_POSITION_ATTRIBUTE.toLowerCase():
                this._tooltipPosition = newValue;
                break;
        }

        this.render();
    }

    /**
     * Cache DOM element references for better performance
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._tooltipDropdownContent = this.shadowRoot.getElementById(
            'tooltip-premium-content',
        );
        this._tooltipDropdownContentText = this.shadowRoot.querySelector(
            '#tooltip-premium-content > p',
        );
        this._tooltipImgsContentContainer = this.shadowRoot.querySelector(
            '[data-premium-tooltip-dropdown]',
        );
    }

    /**
     * Render the component
     * @returns {void}
     */
    render() {
        if (
            !this._tooltipDropdownContent ||
            !this._tooltipDropdownContentText ||
            !this._tooltipImgsContentContainer
        ) {
            return;
        }

        // Apply tooltip content width
        if (this._tooltipContentWidth) {
            this._tooltipDropdownContent.style.maxWidth = `${this._tooltipContentWidth}px`;
        }

        // Update tooltip content
        updateTooltipContent(this._tooltipDropdownContentText);

        // Apply tooltip positioning
        applyTooltipPositioning(
            this._tooltipImgsContentContainer,
            this._tooltipPosition,
            this._customFlexGrow,
        );
    }

    /**
     * Translate the component elements
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Add event listeners to the component elements
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
    }

    /**
     * Remove event listeners to prevent memory leaks
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
    }

    /**
     * Add the ripple effect to elements with data-ripple attribute
     * @returns {void}
     * @private
     */
    _addRippleBehavior() {
        const rippleElements = this.shadowRoot.querySelectorAll('[data-ripple]');
        rippleElements.forEach((element) => {
            ripple(element);
        });
    }
}

customElements.define(PREMIUM_ICON_WEB_COMPONENT, PremiumIconComponent);
